/* eslint-disable max-lines-per-function */
import { useEffect, useRef } from 'react';

import type { ChatSidebarMode } from '@/contexts/ChatSidebarContextDef';
import { useChatSidebar } from '@/contexts/useChatSidebar';
import { ChatMessage } from '@/hooks/useChat';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { cn } from '@/lib/utils';

import { ContextSelectionItem } from '@/components/chat/ContextSelectionContext';
import type { FileContext } from '@/components/chat/parseAttachedFiles';
import { useClearFilesOnConversationChange } from '@/components/chat/useClearFilesOnConversationChange';
import { useFileAttachments } from '@/components/chat/useFileAttachments';

import { ChatEmptyState } from './ChatEmptyState';
import { MessageInput } from './MessageInput';
import { MessageList } from './MessageList';
import { Shortcuts } from './Shortcuts';

interface ChatMessagesAreaProps {
  messages: ChatMessage[];
  showEmptyState: boolean;
  isMessageInputDisabled: boolean;
  isResponding: boolean;
  currentConversationId: string | null;
  userName?: string;
  mode?: ChatSidebarMode;
  onSendMessage: (
    message: string,
    contextSelection?: ContextSelectionItem[],
    fileContexts?: FileContext[]
  ) => Promise<boolean>;
  onStopResponding?: () => void;
  onEnsureConversation?: () => Promise<unknown>;
}

export const ChatMessagesArea = ({
  messages,
  showEmptyState,
  isMessageInputDisabled,
  isResponding,
  currentConversationId,
  userName,
  mode,
  onSendMessage,
  onStopResponding,
  onEnsureConversation,
}: ChatMessagesAreaProps) => {
  const { draftMessage, setDraftMessage, pageContext } = useChatSidebar();
  const supabase = useSupabaseClient();
  const fileAttachments = useFileAttachments(supabase, currentConversationId);

  useClearFilesOnConversationChange(
    currentConversationId,
    fileAttachments.clearFiles
  );

  const creatingConversationRef = useRef(false);
  useEffect(() => {
    if (
      !currentConversationId &&
      onEnsureConversation &&
      !creatingConversationRef.current &&
      fileAttachments.attachedFiles.length > 0 &&
      fileAttachments.attachedFiles.some(
        (file) => file.status === 'parsing' || file.status === 'parsed'
      )
    ) {
      creatingConversationRef.current = true;
      onEnsureConversation().finally(() => {
        creatingConversationRef.current = false;
      });
    }
  }, [
    currentConversationId,
    fileAttachments.attachedFiles,
    onEnsureConversation,
  ]);

  const { isFeatureEnabled } = useFeatureFlag('contextual-shortcuts');

  return (
    <div
      className={cn(
        'flex-1 flex flex-col min-h-0',
        showEmptyState ? 'justify-end overflow-auto' : 'overflow-hidden'
      )}
    >
      {showEmptyState ? (
        <ChatEmptyState userName={userName} mode={mode} />
      ) : (
        <MessageList
          messages={messages}
          isResponding={isResponding}
          conversationId={currentConversationId}
          mode={mode}
        />
      )}

      <div className="flex-shrink-0">
        {isFeatureEnabled && showEmptyState && (
          <Shortcuts key={pageContext?.pathname ?? ''} />
        )}
        <MessageInput
          key={currentConversationId}
          onSendMessage={onSendMessage}
          disabled={isMessageInputDisabled}
          placeholder="Ask Stella anything and/or add context with @"
          value={draftMessage}
          onChange={setDraftMessage}
          fixedHeight={mode === 'floating'}
          mode={mode}
          fileAttachments={fileAttachments}
          isResponding={isResponding}
          onStopResponding={onStopResponding}
        />
      </div>
    </div>
  );
};
